/**
 * LoyaltyTransactionsHistory - List of loyalty point transactions with reasons and balances
 */

import { useTranslation } from 'react-i18next';

export interface LoyaltyTransaction {
  transactionId: string;
  userId: string;
  points: number;
  reason: string;
  queueId: string;
  balanceBefore: number;
  balanceAfter: number;
  createdAt: Date;
}

interface LoyaltyTransactionsHistoryProps {
  transactions: LoyaltyTransaction[];
  loading: boolean;
}

export default function LoyaltyTransactionsHistory({ transactions, loading }: LoyaltyTransactionsHistoryProps) {
  const { t } = useTranslation();

  const getReasonText = (reason: string) => {
    const reasons: Record<string, string> = {
      completed_service: t('profilePage.reasons.completedService'),
      no_show: t('profilePage.reasons.noShow'),
      expired: t('profilePage.reasons.expired'),
      late_cancellation: t('profilePage.reasons.lateCancellation'),
    };
    return reasons[reason] || reason;
  };

  const getReasonColor = (points: number) => {
    if (points > 0) return 'text-success';
    if (points < 0) return 'text-danger';
    return 'text-ink-muted';
  };

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6">
      <h2 className="font-sans text-lg font-semibold text-ink mb-4">{t('profilePage.pointsHistory')}</h2>

      {loading ? (
        <p className="text-sm text-ink-muted">{t('profilePage.loadingHistory')}</p>
      ) : transactions.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-ink-muted">{t('profilePage.noTransactions')}</p>
          <p className="text-sm text-ink-subtle mt-2">
            {t('profilePage.transactionsNote')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map((tx) => (
            <div
              key={tx.transactionId}
              className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2 hover:border-hairline-strong transition-colors"
            >
              <div className="flex-1">
                <p className="font-medium text-ink">
                  {getReasonText(tx.reason)}
                </p>
                <p className="text-sm text-ink-muted mt-1">
                  {tx.createdAt?.toLocaleString('es-AR', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              <div className="text-right ml-4">
                <p className={`text-2xl font-semibold tabular-nums ${getReasonColor(tx.points)}`}>
                  {tx.points > 0 ? '+' : ''}{tx.points}
                </p>
                <p className="text-xs text-ink-subtle mt-1 tabular-nums">
                  {t('profilePage.balance')}: {tx.balanceBefore} → {tx.balanceAfter}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
